import type { StudyFeedback } from './study.ts';
import type { OrderWordItem } from './order-words.ts';

export type StudyAnswerStatus = 'correct' | 'incorrect';

/**
 * Derives the visible result for one presented choice option from the
 * authoritative options and the learner's submitted selection.
 */
export function choiceOptionStatus(
  feedback: StudyFeedback | null,
  selectedOptionIds: readonly string[],
  optionId: string,
): StudyAnswerStatus | undefined {
  if (!feedback) return undefined;
  const selected = selectedOptionIds.includes(optionId);
  const optionIsCorrect =
    feedback.correctOptions?.some((option) => option.id === optionId) ?? false;
  return selected === optionIsCorrect ? 'correct' : 'incorrect';
}

/** Returns the visible result for a submitted word at a workspace position. */
export function orderPositionStatus(
  feedback: StudyFeedback | null,
  workspace: readonly OrderWordItem[],
  item: OrderWordItem,
  position: number,
): StudyAnswerStatus | undefined {
  if (!feedback) return undefined;
  const expected =
    feedback.correctOrder ?? workspace.map((entry) => entry.sourceIndex);
  return expected[position] === item.sourceIndex ? 'correct' : 'incorrect';
}

/** Maps post-answer authoritative source indices back to displayed word items. */
export function canonicalOrderWords(
  items: readonly OrderWordItem[],
  correctOrder: readonly number[] | undefined,
): OrderWordItem[] | undefined {
  if (!correctOrder) return undefined;
  return correctOrder.flatMap((sourceIndex) => {
    const item = items.find((entry) => entry.sourceIndex === sourceIndex);
    return item ? [item] : [];
  });
}
